<template>
  <Button
    v-bind="$attrs"
    :appearance="resolvedAppearance"
    :hierarchy="resolvedHierarchy"
    :size="resolvedSize"
    :disabled="props.disabled"
    :loading="props.loading"
    :icon-only="props.iconOnly"
    :right-icon="props.rightIcon"
    :label="resolvedLabel"
    :type="props.type"
    :color="resolvedColor"
    :haptic-feedback="props.hapticFeedback"
    :icon="props.icon"
    @click="
      (e: MouseEvent) => {
        $emit('click', e)
        $emit('on-click', e)
      }
    "
  />
  <!--
    DS Button renders {{ label }} and has no default slot. We keep the
    legacy slot API by rendering it into a hidden span, then extracting
    its trimmed textContent to feed the `label` prop.
  -->
  <span v-show="false" ref="slotRef"><slot /></span>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUpdated, ref, type Component } from 'vue'
import { Button } from '@p-utilities/vue'
import type { ButtonAppearance, ButtonHierarchy, ButtonSize } from '@p-utilities/vue'

defineEmits(['click', 'on-click'])

const props = defineProps<{
  appearance?: ButtonAppearance
  hierarchy?: ButtonHierarchy
  size?: ButtonSize | 'xxs' | 'xs' | 'sm' | 'md' | 'lg'
  disabled?: boolean
  loading?: boolean
  iconOnly?: boolean
  rightIcon?: boolean
  label?: string
  type?: 'button' | 'submit' | 'reset'
  color?: string
  hapticFeedback?: boolean
  icon?: Component

  variant?: 'filled' | 'ghost' | 'text'

  buttonType?: 'filled' | 'ghost' | 'text'
  status?: 'success' | 'danger'
}>()

const resolvedHierarchy = computed<ButtonHierarchy>(() => {
  if (props.hierarchy) return props.hierarchy
  const v = props.variant ?? props.buttonType
  if (v === 'ghost' || v === 'text') return 'Ghost'
  if (v === 'filled') return 'Primary'
  return 'Primary'
})

const resolvedAppearance = computed<ButtonAppearance>(() => {
  if (props.appearance) return props.appearance
  if (props.color === 'red') return 'Red'
  if (props.color === 'green') return 'Accent'
  if (props.status === 'danger') return 'Red'
  if (props.status === 'success') return 'Accent'
  return 'Accent'
})

// `color` is overloaded: DS uses it as a CSS color override; we also accept
// it as an `appearance` shortcut ('green' | 'red'). When the value is an
// alias, don't forward it as a CSS override — appearance already handled it.
const resolvedColor = computed<string | undefined>(() => {
  const c = props.color
  if (c === 'red' || c === 'green') return undefined
  return c
})

const SIZE_MAP: Record<string, ButtonSize> = {
  xxs: 'X-Small',
  xs: 'X-Small',
  sm: 'Small',
  md: 'Medium',
  lg: 'Large',
}

const resolvedSize = computed<ButtonSize>(() => {
  const s = props.size
  if (!s) return 'Large'
  if (s === 'X-Small' || s === 'Small' || s === 'Medium' || s === 'Large') return s
  return SIZE_MAP[s] ?? 'Large'
})

// Slot-text capture so callers can pass label content as default-slot children.
const slotRef = ref<HTMLElement>()
const slotText = ref('')

const updateSlotText = () => {
  if (slotRef.value) {
    slotText.value = slotRef.value.textContent?.trim() ?? ''
  }
}

onMounted(updateSlotText)
onUpdated(updateSlotText)

const resolvedLabel = computed(() => {
  if (props.iconOnly) return ''
  if (props.label) return props.label
  return slotText.value || ''
})
</script>
